import { FormFieldLabel } from "#components/ui/FormFieldLabel.tsx";
import { AddMemoryInput } from "#features/personalization/components/AddMemoryInput.tsx";
import { SavedMemoriesEmptyState } from "#features/personalization/components/SavedMemoriesEmptyState.tsx";
import { SavedMemoryItem } from "#features/personalization/components/SavedMemoryItem.tsx";
import type { UpdatePersonalizationForm } from "#features/personalization/hooks/useUpdatePersonalizationForm.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { Button, Div, Divider, Flex, Text } from "@hopper-ui/components";
import { useCallback } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";

export const SavedMemories = () => {
    const { tShared } = useSharedTranslation();
    const { control } = useFormContext<UpdatePersonalizationForm>();
    const { fields, prepend, remove, update } = useFieldArray({
        control,
        name: "memories"
    });

    const handleAdd = useCallback((value: string) => {
        prepend({ memoryId: null, value });
    }, [prepend]);

    const handleDelete = useCallback((id: string) => {
        const index = fields.findIndex(field => field.id === id);
        if (index !== -1) {
            remove(index);
        }
    }, [fields, remove]);

    const handleUpdate = useCallback((id: string, value: string) => {
        const index = fields.findIndex(field => field.id === id);
        if (index !== -1) {
            const updatedMemory = { ...fields[index], value };
            update(index, updatedMemory);
        }
    }, [fields, update]);

    const handleDeleteAll = useCallback(() => {
        remove();
    }, [remove]);

    return (
        <Flex direction="column" gap="inline-md">
            <Flex justifyContent="space-between" alignItems="center" wrap="wrap">
                <FormFieldLabel tooltip={tShared("personalization.memoriesTooltip")}>
                    <Text fontWeight="body-md-medium">
                        {tShared("personalization.memoriesLabel")}
                    </Text>
                </FormFieldLabel>
                <Button
                    onPress={handleDeleteAll}
                    variant="ghost-secondary"
                    size="sm"
                    visibility={fields.length === 0 ? "hidden" : "visible"}
                >
                    {tShared("personalization.deleteAllMemories")}
                </Button>
            </Flex>
            <Flex
                direction="column"
                border="neutral-weak"
                borderRadius="rounded-md"
                padding="inset-md"
            >
                <AddMemoryInput onAdd={handleAdd} />
                <Divider marginY="inline-md" />
                <Flex direction="column">
                    {fields.length === 0 && <SavedMemoriesEmptyState />}
                    {fields.map((memory, index) => {
                        const showDivider = index < fields.length - 1;

                        return (
                            <Div key={memory.id}>
                                <SavedMemoryItem
                                    id={memory.id}
                                    memory={memory}
                                    onDelete={handleDelete}
                                    onUpdate={handleUpdate}
                                />
                                {showDivider && <Divider marginY="inline-md" />}
                            </Div>
                        );
                    })}
                </Flex>
            </Flex>
        </Flex>
    );
};
